import React, { useState, useEffect } from 'react'
import BankAccountForm from '../components/BankAccountForm'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const BankAccountsPage = () => {
  const [bankAccounts, setBankAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)

  useEffect(() => {
    loadBankAccounts()
  }, [])

  const loadBankAccounts = async () => {
    try {
      setLoading(true)
      const result = await apiService.getBankAccounts()
      console.log('Contas bancárias carregadas:', result.data)
      setBankAccounts(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar contas bancárias:', error)
      setBankAccounts([])
      toast.error('Erro ao carregar contas bancárias')
    } finally {
      setLoading(false)
    }
  }

  const filteredAccounts = bankAccounts.filter(account => {
    const searchLower = searchQuery.toLowerCase()
    return (
      account.name.toLowerCase().includes(searchLower) ||
      account.bank_name.toLowerCase().includes(searchLower) ||
      account.account_number.includes(searchQuery) ||
      account.agency.includes(searchQuery)
    )
  })

  const handleAdd = () => {
    setEditingAccount(null)
    setShowForm(true)
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    setShowForm(true)
  }

  const handleView = (account) => {
    toast(`Visualizando conta: ${account.name}`, {
      icon: 'ℹ️',
    })
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja desativar a conta bancária "${name}"?`)) {
      return
    }

    try {
      await apiService.deleteBankAccount(id)
      toast.success('Conta bancária desativada com sucesso!')
      loadBankAccounts()
    } catch (error) {
      console.error('Erro ao desativar conta bancária:', error)
      toast.error('Erro ao desativar conta bancária')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingAccount(null)
  }

  const handleFormSuccess = () => {
    loadBankAccounts()
    setShowForm(false)
    setEditingAccount(null)
  }

  const getAccountTypeLabel = (type) => {
    const types = {
      'checking': 'Conta Corrente',
      'savings': 'Poupança',
      'investment': 'Investimento',
      'business': 'Conta Empresarial'
    }
    return types[type] || type
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando contas bancárias...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BuildingLibraryIcon className="h-8 w-8 text-blue-600 mr-3" />
            Contas Bancárias
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie as contas bancárias da empresa
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nova Conta</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, banco, agência ou conta..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Contas</p>
              <p className="text-2xl font-bold text-gray-900">{bankAccounts.length}</p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Contas Correntes</p>
              <p className="text-2xl font-bold text-gray-900">
                {bankAccounts.filter(acc => acc.account_type === 'checking').length}
              </p>
            </div>
            <BuildingLibraryIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Poupanças</p>
              <p className="text-2xl font-bold text-gray-900">
                {bankAccounts.filter(acc => acc.account_type === 'savings').length}
              </p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Com PIX</p>
              <p className="text-2xl font-bold text-gray-900">
                {bankAccounts.filter(acc => acc.pix_key).length}
              </p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Lista de Contas */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredAccounts.length === 0 ? (
          <div className="text-center py-12">
            <BuildingLibraryIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhuma conta encontrada' : 'Nenhuma conta bancária cadastrada'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Tente ajustar os filtros de busca.'
                : 'Comece cadastrando uma nova conta bancária.'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={handleAdd}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Cadastrar primeira conta</span>
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Banco
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agência/Conta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PIX
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {account.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {account.id}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.bank_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getAccountTypeLabel(account.account_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.agency} / {account.account_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.pix_key ? (
                        <span className="text-green-600">✓ {account.pix_key}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(account)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(account)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(account.id, account.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Desativar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <BankAccountForm
          account={editingAccount}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default BankAccountsPage